import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { disposeApi } from "@/api";
import { Subtitle } from "@/components/dialog/DialogLayout";
import { FormFieldError } from "@/components/form/FormField";
import { ListItem } from "@/components/list/ListItem";
import { TeamListItem } from "@/components/team/TeamListItem";
import { UserListItem } from "@/components/user/UserListItem";
import { Email } from "@/components/user/UserSearchInput";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";
import { useIsMyDoc } from "@/utils/useIsMyDoc";

import { AcceptDeclineButtons } from "../components/AcceptDeclineButtons";
import { EmailPermissionDropdown } from "../components/EmailPermissionDropdown";
import { PermissionDropdown } from "../components/PermissionDropdown";

import type { DocumentPermission, TeamShort, UserShort } from "@/api";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ReactNode } from "react";

const PermissionItemWrapper = ({
  children,
  errorMessage,
}: {
  children: ReactNode;
  errorMessage: string;
}) => {
  return (
    <>
      <div className="flex flex-row items-center min-w-0 min-h-12">{children}</div>
      <FormFieldError message={errorMessage} className="mt-0" />
    </>
  );
};

const EmailPermissionItem = ({ permission }: { permission: DocumentPermission }) => {
  const [errorMessage, setErrorMessage] = useState("");

  const handleError = (error: string) => {
    setErrorMessage(error);
  };

  return (
    <PermissionItemWrapper errorMessage={errorMessage}>
      <ListItem
        content={<Email email={permission.email || ""} />}
        rightController={
          <>
            {/* TODO: if we change permission scope here token stops working. waiting for BE fix */}
            {/* <PermissionDropdown permission={permission} onError={handleError} /> */}
            <EmailPermissionDropdown permission={permission} onError={handleError} />
          </>
        }
      />
    </PermissionItemWrapper>
  );
};

const PermissionItem = ({
  team,
  request,
  access,
}: {
  team: TeamShort;
  request?: DocumentPermission;
  access?: DocumentPermission;
}) => {
  const [errorMessage, setErrorMessage] = useState("");

  const handleError = (error: string) => {
    setErrorMessage(error);
  };

  return (
    <>
      <PermissionItemWrapper errorMessage={errorMessage}>
        <TeamListItem
          team={team}
          isLink
          invertedHoverColor
          rightController={
            access ? <PermissionDropdown permission={access} onError={handleError} /> : undefined
          }
        />
      </PermissionItemWrapper>
      {request ? <AcceptDeclineButtons request={request} permission={access} /> : null}
    </>
  );
};

export const PermissionsContent = observer(function PermissionsContent({
  doc,
}: {
  doc: DocumentModel;
}) {
  const t = useTranslations();
  const { auth } = useStore();
  const { permissionsToRender } = doc.permissions;
  const isMyDoc = useIsMyDoc(doc);

  useEffect(() => {
    if (!isMyDoc) {
      return;
    }
    return disposeApi(async (signal) => {
      await doc.permissions.fetchPermissionsAndRequests(signal);
    });
  }, [doc, isMyDoc]);

  const owner = doc.team
    ? ({
        uid: doc.team.uid,
        typename: "UserShort" as const,
        name: doc.team.title || "",
        username: doc.team.permalink,
        avatarUrls: doc.team.avatarUrls,
      } satisfies UserShort)
    : null;
  const userIsMember = auth.currentUser?.username === owner?.username;

  return (
    <div className="flex flex-1 flex-col min-h-0 pt-4 px-0.5 overflow-auto sm:min-h-auto sm:overflow-visible">
      {doc.permissions.emails.length > 0 && (
        <div className="mb-2.5">
          <Subtitle title={t("share.invited")} className="mb-1.5 px-2" />
          {doc.permissions.emails.map((email) => (
            <EmailPermissionItem key={email.uid} permission={email} />
          ))}
        </div>
      )}
      <Subtitle title={t("share.peopleWithAccess")} className="mb-1.5 px-2" />
      {owner && (
        <UserListItem
          user={owner}
          isLink
          invertedHoverColor
          rightController={
            <div
              className={twMerge(
                "flex h-9.5 px-2.5 text-gray-500 text-[15px] leading-[1.2] text-right tracking-tight",
                "sm:text-[17px]",
              )}
            >
              <div className="m-auto">{t("teamDialog.statusOwner")}</div>
            </div>
          }
          namePostfix={userIsMember ? `(${t("teamDialog.itIsYou")})` : ""}
        />
      )}
      {permissionsToRender.map((entry) => {
        const key = entry.team?.permalink ?? entry.email ?? entry.access?.uid ?? entry.request?.uid;
        if (entry.team) {
          return (
            <PermissionItem
              key={key}
              team={entry.team}
              request={entry.request}
              access={entry.access}
            />
          );
        }
        return null;
      })}
    </div>
  );
});
